// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@pace/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    buttonLabel: string;
    title: string;
    subtitle: string;
    buttonLink: LinkFieldValue;
    image: string;
    imageCaption?: string;
    extendedStyles?: InfoCardStyles;
}

type Props = OwnProps;

export interface InfoCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    cardContentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const infoCardStyles: InfoCardStyles = {};

const InfoCard: React.FC<Props> = ({
    buttonLabel,
    title,
    subtitle,
    buttonLink,
    image,
    imageCaption,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(infoCardStyles, extendedStyles));

    const link = useGetLink(buttonLink);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={image} {...styles.image} altText={imageCaption || "Pace Card Image"} />
            </StyledWrapper>
            <StyledWrapper {...styles.cardContentWrapper}>
                <StyledWrapper>
                    <Typography {...styles.title}>{title}</Typography>
                    <Typography {...styles.subtitle}>{subtitle}</Typography>
                </StyledWrapper>
                {link && (
                    <Link href={link.url} {...styles.link} aria-label={`${buttonLabel} ${link.title}`}>
                        {buttonLabel || link.title || link.url}
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default InfoCard;
